import { createComponent } from '@lit/react'
import * as React from 'react'

import Component from '../../components/icon-button/icon-button.component.js'

const tagName = 'dap-ds-icon-button'

/**
 * `dap-ds-icon-button`
 * @summary An icon button is a button with an icon.
 * @element dap-ds-icon-button
 * @title - Icon button
 *
 * @property {string} ariaLabel - The aria label of the button.
 *
 * @slot - The icon of the button.
 *
 * @csspart base - The main button container.
 * @csspart content - The icon of the button.
 *
 * @cssproperty --dds-icon-button-border-width - The width of the button's border. Defaults to var(--dds-border-width-base).
 * @cssproperty --dds-icon-button-border-color - The color of the button's border. Defaults to var(--dds-border-neutral-transparent-interactive).
 * @cssproperty --dds-icon-button-border-radius - The border radius of the button. Defaults to var(--dds-radius-rounded).
 * @cssproperty --dds-icon-button-hover-border-width - The width of the button's border on hover. Defaults to var(--dds-border-width-large).
 * @cssproperty --dds-icon-button-active-border-width - The width of the button's border when active. Defaults to var(--dds-border-width-xlarge).
 * @cssproperty --dds-icon-button-transition - The transition property for the button. Defaults to var(--dds-transition-all).
 * @cssproperty --dds-icon-button-cursor - The cursor style for the button. Defaults to pointer.
 * @cssproperty --dds-icon-button-disabled-cursor - The cursor style for the disabled button. Defaults to not-allowed.
 *
 * @cssproperty --dds-icon-button-column-gap - The gap between icon and text. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-icon-button-padding-lg - Padding for large size. Defaults to var(--dds-spacing-0).
 * @cssproperty --dds-icon-button-padding-md - Padding for medium size. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-icon-button-padding-sm - Padding for small size. Defaults to var(--dds-spacing-50).
 * @cssproperty --dds-icon-button-padding-xs - Padding for extra small size. Defaults to var(--dds-spacing-25).
 *
 * @cssproperty --dds-icon-button-size-lg - Size for large button. Defaults to var(--dds-spacing-800).
 * @cssproperty --dds-icon-button-size-md - Size for medium button. Defaults to var(--dds-spacing-600).
 * @cssproperty --dds-icon-button-size-sm - Size for small button. Defaults to var(--dds-spacing-400).
 * @cssproperty --dds-icon-button-size-xs - Size for extra small button. Defaults to var(--dds-spacing-300).
 *
 * @cssproperty --dds-icon-button-neutral-bg - Background color for neutral variant. Defaults to var(--dds-transparent-black-subtle).
 * @cssproperty --dds-icon-button-neutral-color - Text color for neutral variant. Defaults to var(--dds-icon-neutral-base).
 * @cssproperty --dds-icon-button-neutral-hover-bg - Hover background color for neutral variant. Defaults to var(--dds-transparent-black-base).
 * @cssproperty --dds-icon-button-neutral-active-bg - Active background color for neutral variant. Defaults to var(--dds-transparent-black-medium).
 *
 * @cssproperty --dds-icon-button-brand-bg - Background color for brand variant. Defaults to var(--dds-button-subtle-background-enabled).
 * @cssproperty --dds-icon-button-brand-color - Text color for brand variant. Defaults to var(--dds-button-subtle-icon-enabled).
 * @cssproperty --dds-icon-button-brand-hover-bg - Hover background color for brand variant. Defaults to var(--dds-button-subtle-background-hover).
 * @cssproperty --dds-icon-button-brand-active-bg - Active background color for brand variant. Defaults to var(--dds-button-subtle-background-pressed).
 *
 * @cssproperty --dds-icon-button-inverted-bg - Background color for inverted variant. Defaults to var(--dds-transparent-white-subtle).
 * @cssproperty --dds-icon-button-inverted-color - Text color for inverted variant. Defaults to var(--dds-button-primary-icon-enabled).
 * @cssproperty --dds-icon-button-inverted-hover-bg - Hover background color for inverted variant. Defaults to var(--dds-transparent-white-base).
 * @cssproperty --dds-icon-button-inverted-active-bg - Active background color for inverted variant. Defaults to var(--dds-transparent-white-medium).
 *
 * @cssproperty --dds-icon-button-inverted-brand-bg - Background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-color - Text color for inverted brand variant. Defaults to var(--dds-button-primary-icon-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-hover-bg - Hover background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-active-bg - Active background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 *
 * @cssproperty --dds-icon-button-disabled-color - Color for disabled state. Defaults to var(--dds-icon-neutral-disabled).
 */
const reactWrapper = createComponent({
  tagName,
  elementClass: Component,
  react: React,
  events: {},
  displayName: 'DapDSIconButton',
})

export default reactWrapper
